import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Avatar,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import {
  Save as SaveIcon,
  AccountCircle as AccountCircleIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import authService from '../services/auth.service';
import UserService from '../services/user.service';

const Profile: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState<any>(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    preferredLanguage: 'en',
  });

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      setError('');
      // Fetch user with role populated
      const response = await UserService.me();
      const userData = response.data || response;
      
      // If role is not populated, try to get it from the current user
      if (!userData.role || (!userData.role.name && !userData.role.type)) {
        const currentUser = authService.getCurrentUser();
        if (currentUser?.role) {
          userData.role = currentUser.role;
        }
      }
      
      setUser(userData);
      setFormData({
        username: userData.username || '',
        email: userData.email || '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        preferredLanguage: userData.preferredLanguage || 'en',
      });
    } catch (err: any) {
      console.error('Error fetching user profile:', err);
      setError(err.response?.data?.error?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      if (!user?.id) {
        throw new Error('User ID not found');
      }

      const updateData: any = {
        username: formData.username,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        preferredLanguage: formData.preferredLanguage,
      };

      await UserService.update(user.id, updateData);
      
      // Update local storage
      const updatedUser = {
        ...user,
        ...updateData,
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Update language if changed
      if (formData.preferredLanguage !== i18n.language) {
        i18n.changeLanguage(formData.preferredLanguage);
      }

      setSuccess('Profile updated successfully');
      setUser(updatedUser);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err.response?.data?.error?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        {t('user.profile')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        {/* Profile Information Card */}
        <Box sx={{ flex: { md: 2 } }}>
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
              <AccountCircleIcon color="primary" sx={{ fontSize: 32 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {t('user.profile')}
              </Typography>
            </Stack>

            <Divider sx={{ mb: 3 }} />

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                  <TextField
                    fullWidth
                    label={t('auth.username')}
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                  />

                  <TextField
                    fullWidth
                    label={t('auth.email')}
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                  <TextField
                    fullWidth
                    label={t('user.firstName')}
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                  />

                  <TextField
                    fullWidth
                    label={t('user.lastName')}
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </Stack>

                <TextField
                  fullWidth
                  select
                  label="Preferred Language"
                  name="preferredLanguage"
                  value={formData.preferredLanguage}
                  onChange={handleChange}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="ar">العربية (Arabic)</MenuItem>
                  <MenuItem value="fr">Français (French)</MenuItem>
                  <MenuItem value="es">Español (Spanish)</MenuItem>
                </TextField>

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={saving}
                    sx={{
                      backgroundColor: '#F56A2C',
                      '&:hover': {
                        backgroundColor: '#D95C26',
                      },
                    }}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Stack>
              </Stack>
            </form>
          </Paper>
        </Box>

        {/* Account Information Card */}
        <Box sx={{ flex: { md: 1 }, minWidth: { md: 300 } }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <SecurityIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Account Information
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    User ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {user?.id || 'N/A'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Role
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {user?.role?.name || 
                     user?.role?.type || 
                     user?.role?.attributes?.name ||
                     user?.role?.attributes?.type ||
                     (user?.role?.id ? `Role ID: ${user.role.id}` : 'N/A')}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Account Status
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {user?.confirmed !== false ? (
                      <span style={{ color: '#4caf50' }}>Confirmed</span>
                    ) : (
                      <span style={{ color: '#f44336' }}>Not Confirmed</span>
                    )}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Blocked
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {user?.blocked ? (
                      <span style={{ color: '#f44336' }}>Yes</span>
                    ) : (
                      <span style={{ color: '#4caf50' }}>No</span>
                    )}
                  </Typography>
                </Box>

                {user?.createdAt && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Member Since
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}

                {user?.lastLoginAt && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Last Login
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {new Date(user.lastLoginAt).toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Profile Avatar Card */}
          <Card>
            <CardContent>
              <Stack alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    bgcolor: '#F56A2C',
                    fontSize: '3rem',
                  }}
                >
                  {formData.firstName && formData.lastName
                    ? `${formData.firstName[0]}${formData.lastName[0]}`.toUpperCase()
                    : formData.username?.[0]?.toUpperCase() || 'U'}
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                  {formData.firstName && formData.lastName
                    ? `${formData.firstName} ${formData.lastName}`
                    : formData.username || 'User'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  {formData.email}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Stack>
    </Box>
  );
};

export default Profile;

